import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Cloud, RefreshCw, CheckCircle2, AlertTriangle, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * AutoBackupCard
 *
 * UI für das automatisierte tägliche MongoDB-Backup nach Dropbox.
 * - Zeigt Status der letzten Sicherung (Zeitstempel, Pfad, Größe, Fehler)
 * - Toggle „Automatisch aktivieren", Uhrzeit (UTC) + Aufbewahrungsdauer
 * - Button „Jetzt sofort sichern" für sofortigen Test des Pfads (Dropbox-Verbindung,
 *   Schreibrechte, Quota) — fängt Probleme ab, bevor der nächtliche Cron-Job läuft.
 *
 * Backend: GET /api/settings/auto-backup, POST .../auto-backup, POST .../auto-backup/run
 */
export function AutoBackupCard() {
    const [cfg, setCfg] = useState({ enabled: true, hour_utc: 2, retention_days: 30 });
    const [status, setStatus] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [runningNow, setRunningNow] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get('/settings/auto-backup');
            setCfg({
                enabled: !!data.enabled,
                hour_utc: data.hour_utc ?? 2,
                retention_days: data.retention_days ?? 30,
            });
            setStatus(data.status || {});
        } catch (e) {
            // ignore – Settings sind optional
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.post('/settings/auto-backup', cfg);
            toast.success('Auto-Backup-Einstellungen gespeichert');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    const runNow = async () => {
        setRunningNow(true);
        toast.info('Backup läuft … kann je nach Datenmenge wenige Sekunden bis ~1 Minute dauern.');
        try {
            const { data } = await api.post('/settings/auto-backup/run');
            if (data.status === 'success') {
                toast.success(`Backup erfolgreich nach ${data.last_path} hochgeladen`);
            } else {
                toast.error(data.reason || 'Backup fehlgeschlagen');
            }
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Backup fehlgeschlagen');
        } finally {
            setRunningNow(false);
        }
    };

    const fmt = (iso) => {
        if (!iso) return '—';
        try { return new Date(iso).toLocaleString('de-DE'); }
        catch { return iso; }
    };
    const fmtBytes = (n) => {
        if (!n) return '—';
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
        return `${(n / 1024 / 1024).toFixed(2)} MB`;
    };

    const lastResult = status.last_result;
    const statusBadge = !lastResult ? null
        : lastResult === 'success' ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200"><CheckCircle2 className="h-3 w-3 mr-1" /> erfolgreich</Badge>
        ) : (
            <Badge className="bg-rose-100 text-rose-800 border-rose-200"><AlertTriangle className="h-3 w-3 mr-1" /> Fehler</Badge>
        );

    return (
        <Card className="border-sky-200" data-testid="auto-backup-card">
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                    <Cloud className="h-5 w-5 text-sky-700" />
                    Automatisches Datenbank-Backup nach Dropbox
                </CardTitle>
                <CardDescription>
                    Erzeugt täglich ein ZIP-gepacktes JSON-Backup ALLER Mongo-Collections und legt es im
                    verbundenen Dropbox-Konto unter <code className="text-[11px] bg-sky-50 px-1 py-0.5 rounded">/BGPR/_backups/</code> ab.
                    Alte Backups werden gemäß Aufbewahrungsdauer automatisch rotiert.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Einstellungen werden geladen …
                    </div>
                ) : (
                    <>
                        {/* Konfiguration */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-3 flex items-center justify-between bg-sky-50 rounded-lg p-3">
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">Automatischer Versand</p>
                                    <p className="text-xs text-muted-foreground">Wenn deaktiviert, läuft kein nächtlicher Backup-Job. „Jetzt sofort sichern" funktioniert weiterhin.</p>
                                </div>
                                <Switch
                                    checked={cfg.enabled}
                                    onCheckedChange={(v) => setCfg(prev => ({ ...prev, enabled: v }))}
                                    data-testid="auto-backup-enabled-switch"
                                />
                            </div>

                            <div className="space-y-1.5">
                                <Label htmlFor="ab_hour">Uhrzeit (UTC)</Label>
                                <Input
                                    id="ab_hour"
                                    type="number" min={0} max={23}
                                    value={cfg.hour_utc}
                                    onChange={(e) => setCfg(prev => ({ ...prev, hour_utc: Number(e.target.value) }))}
                                    data-testid="auto-backup-hour-input"
                                />
                                <p className="text-[11px] text-muted-foreground">
                                    Empfehlung 02:00 UTC = 03:00 (Winter) / 04:00 (Sommer) deutscher Zeit.
                                </p>
                            </div>

                            <div className="space-y-1.5">
                                <Label htmlFor="ab_retention">Aufbewahrungsdauer (Tage)</Label>
                                <Input
                                    id="ab_retention"
                                    type="number" min={1} max={365}
                                    value={cfg.retention_days}
                                    onChange={(e) => setCfg(prev => ({ ...prev, retention_days: Number(e.target.value) }))}
                                    data-testid="auto-backup-retention-input"
                                />
                                <p className="text-[11px] text-muted-foreground">Standard: 30 Tage. Alles ältere wird in Dropbox automatisch gelöscht.</p>
                            </div>

                            <div className="space-y-1.5">
                                <Label>&nbsp;</Label>
                                <Button onClick={save} disabled={saving} className="w-full" data-testid="auto-backup-save-btn">
                                    {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                                    Einstellungen speichern
                                </Button>
                            </div>
                        </div>

                        {/* Status-Box */}
                        <div className="rounded-lg border bg-slate-50 p-4 space-y-2">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-slate-600" />
                                    Letzte Sicherung
                                </p>
                                {statusBadge}
                            </div>
                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                                <div>
                                    <dt className="text-muted-foreground">Zeitpunkt:</dt>
                                    <dd className="font-mono">{fmt(status.last_run_at)}</dd>
                                </div>
                                <div>
                                    <dt className="text-muted-foreground">Größe:</dt>
                                    <dd className="font-mono">{fmtBytes(status.last_size_bytes)}</dd>
                                </div>
                                <div className="sm:col-span-2">
                                    <dt className="text-muted-foreground">Pfad (Dropbox):</dt>
                                    <dd className="font-mono break-all">{status.last_path || '—'}</dd>
                                </div>
                                {status.last_collections_count ? (
                                    <div>
                                        <dt className="text-muted-foreground">Collections:</dt>
                                        <dd className="font-mono">{status.last_collections_count}</dd>
                                    </div>
                                ) : null}
                                {status.last_deleted_old_backups != null && (
                                    <div>
                                        <dt className="text-muted-foreground">Rotation:</dt>
                                        <dd className="font-mono">{status.last_deleted_old_backups} alte Backups entfernt</dd>
                                    </div>
                                )}
                                {status.last_error ? (
                                    <div className="sm:col-span-2">
                                        <dt className="text-rose-700 font-semibold">Fehler:</dt>
                                        <dd className="font-mono text-rose-900 bg-rose-50 p-2 rounded">{status.last_error}</dd>
                                    </div>
                                ) : null}
                            </dl>
                            <Button
                                onClick={runNow}
                                disabled={runningNow}
                                variant="default"
                                className="bg-sky-600 hover:bg-sky-700"
                                data-testid="auto-backup-run-now-btn"
                            >
                                {runningNow
                                    ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Backup läuft …</>
                                    : <><RefreshCw className="h-4 w-4 mr-1" /> Jetzt sofort sichern</>}
                            </Button>
                            <p className="text-[11px] text-muted-foreground">
                                Tipp: Den manuellen Lauf einmal nach der Konfiguration ausführen — so siehst du sofort,
                                ob Dropbox-Verbindung, Schreibrechte und Quota stimmen, bevor der Cron-Job nachts läuft.
                            </p>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
